// Pieces Operator › Templates shares (DC-03): state badges, labels, the reasons a validation or a
// publish gave, the template-author check and its note for everyone else.
import { ApiError } from "@companyos/sdk";
import { Badge, Notice } from "@companyos/ui";
import type {
  AgentTemplate,
  AgentTemplateVersion,
  MeResponse,
  TemplateEditProposal,
} from "../../../api/types";

type Variant = "neutral" | "info" | "success" | "warning" | "danger" | "outline";

const VERSION_STATE: Record<AgentTemplateVersion["state"], { label: string; variant: Variant }> = {
  draft: { label: "Draft", variant: "neutral" },
  validated: { label: "Validated", variant: "info" },
  published: { label: "Published", variant: "success" },
  deprecated: { label: "Deprecated", variant: "warning" },
};

const TEMPLATE_STATUS: Record<AgentTemplate["status"], { label: string; variant: Variant }> = {
  draft: { label: "No published version", variant: "neutral" },
  published: { label: "Published", variant: "success" },
  deprecated: { label: "Deprecated", variant: "warning" },
};

const PROPOSAL_STATE: Record<TemplateEditProposal["state"], { label: string; variant: Variant }> = {
  proposed: { label: "Proposed", variant: "info" },
  accepted: { label: "Accepted", variant: "success" },
  rejected: { label: "Rejected", variant: "neutral" },
};

export function VersionStateBadge({ state }: { state: AgentTemplateVersion["state"] }) {
  const s = VERSION_STATE[state];
  return (
    <Badge variant={s.variant} dot data-state={state}>
      {s.label}
    </Badge>
  );
}

export function TemplateStatusBadge({ status }: { status: AgentTemplate["status"] }) {
  const s = TEMPLATE_STATUS[status];
  return (
    <Badge variant={s.variant} data-status={status}>
      {s.label}
    </Badge>
  );
}

export function ProposalStateBadge({ state }: { state: TemplateEditProposal["state"] }) {
  const s = PROPOSAL_STATE[state];
  return <Badge variant={s.variant}>{s.label}</Badge>;
}

export const SCOPE_LABEL: Record<AgentTemplate["scope"], string> = {
  platform: "Platform",
  agency: "Agency",
};
export const CLASS_LABEL: Record<AgentTemplate["template_class"], string> = {
  managed: "Managed",
  basic: "Basic",
  empty: "Empty",
};
export const SOURCE_LABEL: Record<AgentTemplateVersion["source"], string> = {
  author: "Author",
  learning_loop: "Learning loop",
};

/** Whether the signed-in agency user holds the agency's `template_author` relation. */
export function isTemplateAuthor(me: MeResponse): boolean {
  return me.agency?.template_author === true;
}

/** What a non-author sees where the authoring actions would be. */
export function AuthorOnlyNote() {
  return (
    <Notice tone="neutral" data-slot="author-only">
      Only template authors in your agency can change the catalog. You can read every version.
    </Notice>
  );
}

/** The reasons the last validation stored on the version (`validation_report.reasons`). */
export function reportReasons(v: AgentTemplateVersion): string[] {
  const reasons = (v.validation_report as { reasons?: unknown } | null)?.reasons;
  return Array.isArray(reasons) ? reasons.filter((r): r is string => typeof r === "string") : [];
}

/** A problem as lines: its detail, then each field error not already in it. */
export function problemLines(error: unknown, fallback: string): string[] {
  if (!(error instanceof ApiError)) return [fallback];
  const p = error.problem;
  const detail = p?.detail ?? p?.title ?? fallback;
  const errors = (p?.errors ?? [])
    .map((e) => (e.path ? `${e.path}: ${e.message}` : e.message))
    .filter((line) => !detail.includes(line));
  return [detail, ...errors];
}

/** `v3`, or a short id when the version is not in the list. */
export function versionLabel(versions: AgentTemplateVersion[], id: string | null): string {
  if (!id) return "—";
  const v = versions.find((x) => x.id === id);
  return v ? `v${v.version}` : `…${id.slice(-6)}`;
}

/** `company …ac01 · run …0099`: a mistake reference, shortened. */
export function mistakeText(ref: {
  company_id: string;
  run_id: string;
  run_decision_id?: string | undefined;
}): string {
  const s = (id: string) => `…${id.slice(-6)}`;
  return `company ${s(ref.company_id)} · run ${s(ref.run_id)}${
    ref.run_decision_id ? ` · decision ${s(ref.run_decision_id)}` : ""
  }`;
}

/** The `lead` role's model, or the only role's (the API's lead rule). */
export function leadModel(map: AgentTemplateVersion["role_model_map"]): string | null {
  const entries = Object.entries(map);
  if (map.lead) return map.lead.model;
  return entries.length === 1 ? (entries[0]?.[1].model ?? null) : null;
}
